import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField } from '@mui/material';
import { memo, useEffect, useState } from 'react';
import { CloseIcon } from '~/assets/icons';
import { useFormik } from 'formik';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import { cast, handleApiError, notify } from '~/utils/helper';
import { useTranslation } from 'react-i18next';
import styles from './EditEstimateTemplateDialog.module.scss';
import classNames from 'classnames/bind';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { IBodyCreateEstimate } from '~/utils/interface/estimate';
import {
  handleCreateEstimateTemplate,
  handleUpdateEstimateTemplate,
} from '~/thunks/estimate-template/estimateTemplateThunk';
import PaymentCalculation from '~/components/estimate/payment-calculation/PaymentCalculation';
import PriceListItem from '~/components/estimate/price-list-item/PriceListItem';
import FileItem from '~/components/estimate/file-item/FileItem';
import { FILE_ITEM_FOR_ESTIMATE_TEMPLATE } from '~/utils/constants/jobs';
import { estimateTemplateActions } from '~/thunks/estimate-template/estimateTemplateSlice';
import { IResponseGetEstimateTemplate } from '~/utils/interface/estimate-template';

const cx = classNames.bind(styles);

type EditEstimateTemplateDialogProps = {
  data: IResponseGetEstimateTemplate | undefined;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
  open: boolean;
  mode?: string;
  editData?: IResponseGetEstimateTemplate | undefined;
};

const initialValues: IBodyCreateEstimate = {
  name: '',
  tax: 0,
  subtotal: 0,
  total: 0,
  estimateItems: [],
};

const EditEstimateTemplateDialog = (props: EditEstimateTemplateDialogProps) => {
  const { mode, data, editData } = props;

  const dispatch = useAppDispatch();
  const { t } = useTranslation(['admin/estimate-template', 'admin/common']);
  const {
    profile: { tax },
  } = useAppSelector((state) => state.auth);
  const [companyTax, setCompanyTax] = useState<number>(Number(tax ?? 0));

  useEffect(() => {
    if (mode === DIALOG_MODE_EDIT) {
      form.setFieldValue('name', cast(editData?.name));
    } else if (mode === DIALOG_MODE_CREATE) {
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, editData]);

  const handleCancel = () => {
    handleSetOpen(false);
    form.resetForm();
  };
  const form = useFormik({
    validateOnChange: false,
    initialValues,
    onSubmit: async (values, { setFieldError }) => {
      values.estimateItems = values.estimateItems?.filter((item) => item.priceItemId).map(({ name, ...rest }) => rest);

      if (mode === DIALOG_MODE_CREATE) {
        try {
          await dispatch(handleCreateEstimateTemplate({ data: values })).unwrap();
          handleSetOpen(false);
          notify('success', t('createSuccess', { ns: 'admin/common' }));
          dispatch(estimateTemplateActions.changeNeedRefreshData(true));
        } catch (e: unknown) {
          handleApiError(e, setFieldError);
        }
        return;
      }
      if (mode === DIALOG_MODE_EDIT) {
        try {
          await dispatch(handleUpdateEstimateTemplate({ id: String(data?.id) ?? '1', data: values })).unwrap();
          dispatch(estimateTemplateActions.changeNeedRefreshData(true));
          notify('success', t('editSuccess', { ns: 'admin/common' }));
          handleSetOpen(false);
        } catch (e: unknown) {
          handleApiError(e, setFieldError);
        }
        return;
      }
    },
  });

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    form.resetForm();

    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
  };

  return (
    <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='xl'>
      <DialogTitle>
        {mode === DIALOG_MODE_CREATE ? t('addTemplate') : t('editTemplate')}
        <IconButton onClick={() => handleSetOpen(false)} className='absolute right-[10px] top-[10px]'>
          <CloseIcon className='w-[10px] h-[10px]' />
        </IconButton>
      </DialogTitle>

      <DialogContent className='p-0'>
        <Box className={cx('create-estimate-container')}>
          <Box className='bg-[#F3F9FF] w-full my-4 p-5 font-[600] text-[14px] border-y-2 border-solid border-y-[#e5eaef]'>
            {t('editEmailDialog.name')}
          </Box>
          <Box className='mt-3 p-5 w-2/3'>
            <TextField
              name='name'
              placeholder={t('editEmailDialog.name')}
              value={form.values.name}
              onChange={form.handleChange}
              error={Boolean(form.errors?.name)}
              helperText={form.errors?.name}
            />
          </Box>
          <PaymentCalculation
            form={form}
            subtotal={cast(form.values.subtotal)}
            totalTax={companyTax}
            editData={editData}
            onChangeTax={setCompanyTax}
          />

          <Box>
            <PriceListItem form={form} editData={editData} companyTax={companyTax} />
          </Box>
          <Box>
            <FileItem form={form} typeData={FILE_ITEM_FOR_ESTIMATE_TEMPLATE} editData={editData} />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions className='justify-end'>
        <Button color='secondary' variant='contained' onClick={() => handleCancel()}>
          {t('cancel', { ns: 'admin/common' })}
        </Button>
        <Button
          color='primary'
          variant='contained'
          onClick={() => {
            form.handleSubmit();
          }}
        >
          {t('save', { ns: 'admin/common' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(EditEstimateTemplateDialog);
